import { storeToRefs } from 'pinia'
import { triggerRef } from 'vue'

import { useCallSessionMediaStore } from '@/entities/call-session-media'
import { isDisplayMediaTrack } from '@/entities/media-devices'
import {
  isBackgroundBlurEnabled,
  PreferenceKey,
  usePreferencesStore,
} from '@/entities/preference'
import { useWebRTC } from '@/entities/webrtc'

import { applyBackgroundBlurForLocalPublish } from './apply-background-blur-for-publish'
import {
  detachBackgroundBlurPipelinesForRestoreStream,
  detachBackgroundBlurProcessor,
  isBackgroundBlurProcessedStream,
} from './maybe-apply-background-blur'
import { scheduleKeyframeBurstAfterReplace } from './request-keyframe-after-replace'

type SessionData = {
  trackForAdd?: MediaStreamTrack
}

const findWebcamSender = (
  connection: RTCPeerConnection | undefined,
  trackId: string | undefined,
  preferredTrackId?: string,
): RTCRtpSender | undefined => {
  if (!connection || connection.connectionState === 'closed') {
    return undefined
  }

  const senders = connection.getSenders()
  return senders.find((sender) => sender.track?.id === trackId)
    ?? (preferredTrackId
      ? senders.find((sender) => sender.track?.id === preferredTrackId)
      : undefined)
    ?? senders.find((sender) => {
      const track = sender.track
      return !!track && track.kind === 'video' && !isDisplayMediaTrack(track)
    })
}

const attachTrackToSession = async (
  sender: RTCRtpSender | undefined,
  sessionData: SessionData | undefined,
  track: MediaStreamTrack,
): Promise<boolean> => {
  if (sender) {
    await sender.replaceTrack(track)
    // Burst: canvas encoder / SFU often need a few IDRs after camera↔blur swap.
    // Never enabled-nudge blur/canvas tracks — stalls SFU encoding for remotes.
    scheduleKeyframeBurstAfterReplace(sender, { allowEnabledNudge: false })
    return true
  }

  if (sessionData?.trackForAdd) {
    sessionData.trackForAdd = track
    return true
  }

  return false
}

/**
 * Toggle blur on live call webcam without a second getUserMedia.
 *
 * Enable: clone the camera track for MediaStreamTrackProcessor, keep the original
 * track alive for instant restore.
 *
 * Concurrent calls are queued and re-run with the latest preference so a disable
 * that arrives during an in-flight enable is not dropped (common in conferences).
 */
let reapplyInFlight: Promise<void> | null = null
let reapplyQueued = false

const runReapplyBackgroundBlurOnActiveSessions = async (): Promise<void> => {
  const preferencesStore = usePreferencesStore()
  const { preferences } = storeToRefs(preferencesStore)
  const enabled = isBackgroundBlurEnabled(preferences.value[PreferenceKey.BACKGROUND_BLUR_ENABLED])
  const { sessions } = useWebRTC()
  const callSessionMediaStore = useCallSessionMediaStore()

  for (const sessionFacade of sessions.value.values()) {
    const currentStream = sessionFacade.localVideoStream.value
    const currentTrack = currentStream?.getVideoTracks()[0]
    if (!currentStream || !currentTrack || currentTrack.readyState !== 'live') {
      continue
    }
    if (isDisplayMediaTrack(currentTrack)) {
      continue
    }

    const connection = sessionFacade.session.connection as RTCPeerConnection | undefined
    const sessionData = sessionFacade.session.data as SessionData | undefined
    const preferredTrackId = callSessionMediaStore.webCamLastTrackId as string | undefined
    // Resolve sender BEFORE long async work / detach, while the current track is still attached.
    let sender = findWebcamSender(connection, currentTrack.id, preferredTrackId)

    if (enabled) {
      if (isBackgroundBlurProcessedStream(currentStream)) {
        continue
      }

      // Drop stale pipelines left from a cancelled enable.
      detachBackgroundBlurPipelinesForRestoreStream(currentStream)

      let processed: MediaStream
      try {
        processed = await applyBackgroundBlurForLocalPublish(currentStream)
      } catch (error) {
        console.warn('[background-blur] failed to apply blur on call session', error)
        continue
      }

      if (processed.id === currentStream.id) {
        continue
      }

      if (!isBackgroundBlurEnabled(preferences.value[PreferenceKey.BACKGROUND_BLUR_ENABLED])) {
        detachBackgroundBlurProcessor(processed)
        continue
      }

      // Session camera may have changed while the pipeline was starting.
      if (sessionFacade.localVideoStream.value !== currentStream) {
        detachBackgroundBlurProcessor(processed)
        continue
      }

      const processedTrack = processed.getVideoTracks()[0]
      if (!processedTrack) {
        detachBackgroundBlurProcessor(processed)
        continue
      }

      sender = sender ?? findWebcamSender(connection, currentTrack.id, preferredTrackId)
      try {
        const attached = await attachTrackToSession(sender, sessionData, processedTrack)
        if (!attached) {
          console.warn('[background-blur] no webcam sender to attach blurred track')
          detachBackgroundBlurProcessor(processed)
          continue
        }
      } catch (error) {
        detachBackgroundBlurProcessor(processed)
        console.warn('[background-blur] replaceTrack failed after blur enable', error)
        continue
      }

      if (
        !isBackgroundBlurEnabled(preferences.value[PreferenceKey.BACKGROUND_BLUR_ENABLED])
        || sessionFacade.localVideoStream.value !== currentStream
      ) {
        detachBackgroundBlurProcessor(processed)
        // Prefer raw back on the sender if we already swapped.
        const restoreTrack = currentStream.getVideoTracks()[0]
        if (restoreTrack?.readyState === 'live') {
          try {
            await attachTrackToSession(
              sender ?? findWebcamSender(connection, processedTrack.id, preferredTrackId),
              sessionData,
              restoreTrack,
            )
          } catch {
            // Best-effort rollback.
          }
        }
        continue
      }

      sessionFacade.localVideoStream.value = processed
      triggerRef(sessionFacade.localVideoStream)
      callSessionMediaStore.webCamLastTrackId = processedTrack.id
      continue
    }

    if (!isBackgroundBlurProcessedStream(currentStream)) {
      // Enable may have finished the pipeline while streamRef still pointed at raw.
      detachBackgroundBlurPipelinesForRestoreStream(currentStream)
      continue
    }

    // Find sender while the blurred track is still on it, then detach.
    sender = sender ?? findWebcamSender(connection, currentTrack.id, preferredTrackId)

    const restoreStream = detachBackgroundBlurProcessor(currentStream)
    const restoreTrack = restoreStream?.getVideoTracks()[0]
    if (!restoreStream || !restoreTrack || restoreTrack.readyState !== 'live') {
      console.warn('[background-blur] restore stream is missing or ended after detach')
      continue
    }

    try {
      const attached = await attachTrackToSession(sender, sessionData, restoreTrack)
      if (!attached) {
        console.warn('[background-blur] no webcam sender to restore raw track')
      }
    } catch (error) {
      console.warn('[background-blur] replaceTrack failed after blur disable', error)
      continue
    }

    sessionFacade.localVideoStream.value = restoreStream
    triggerRef(sessionFacade.localVideoStream)
    callSessionMediaStore.webCamLastTrackId = restoreTrack.id
  }
}

export const reapplyBackgroundBlurOnActiveSessions = async (): Promise<void> => {
  if (reapplyInFlight) {
    reapplyQueued = true
    return reapplyInFlight
  }

  reapplyInFlight = (async () => {
    do {
      reapplyQueued = false
      await runReapplyBackgroundBlurOnActiveSessions()
    } while (reapplyQueued)
  })().finally(() => {
    reapplyInFlight = null
  })

  return reapplyInFlight
}
